/**
 * Mount once in the shell. Renders nothing. While this browser has the automatic publish ON, the
 * sewers' snapshots are republished every 15 minutes AND about a minute after a station writes a
 * slip, a receipt or a QC scan. A browser timer, not a server schedule (the $0 plan has no timer):
 * nobody signed in means nothing publishes. Only reads SharePoint and writes the portal's own tables.
 */
import { useEffect } from "react";
import { publishAllSnapshots } from "./portalPublisher";
import { onPortalStale } from "./portalStale";
import { portalDue, portalEventDelay, portalNextDelay } from "./portalAutoPlan";
import {
  getPortalAutoState, readPortalLastRun, recordPortalAutoResult, setPortalAutoRunning,
  usePortalAutoState, writePortalLastRun,
} from "./portalAutoStore";

export async function runPortalAuto(): Promise<void> {
  setPortalAutoRunning(true);
  const at = Date.now();
  try {
    const r = await publishAllSnapshots();
    recordPortalAutoResult(r.failed.length === 0
      ? { at, status: "ok", message: `published ${r.published} of ${r.sewers} sewers.` }
      : { at, status: "failed", message: `published ${r.published} of ${r.sewers}; ${r.failed.length} failed (${r.failed[0].error}).` });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // A sign-in that is not portal staff is answered 403: nothing to do, and no point retrying loudly.
    recordPortalAutoResult({ at, status: /403|forbidden|staff/i.test(msg) ? "skipped" : "failed", message: msg });
  } finally {
    setPortalAutoRunning(false);
  }
}

export function PortalAutoRunner() {
  const { enabled } = usePortalAutoState();
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: number | undefined;
    let eventTimer: number | undefined;
    const go = async (): Promise<void> => {
      if (!navigator.onLine || getPortalAutoState().running) return;
      // Stamped BEFORE the run so a sibling tab stands down, and a failing run waits its turn.
      writePortalLastRun(Date.now());
      await runPortalAuto();
    };
    const schedule = (): void => { timer = window.setTimeout(() => { void tick(); }, portalNextDelay(Date.now(), readPortalLastRun())); };
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      if (portalDue(Date.now(), readPortalLastRun())) await go();
      if (!cancelled) schedule();
    };
    const off = onPortalStale(() => {
      if (eventTimer !== undefined) return; // a burst of scans is one publish
      eventTimer = window.setTimeout(() => { eventTimer = undefined; if (!cancelled) void go(); },
        portalEventDelay(Date.now(), readPortalLastRun()));
    });
    schedule();
    return () => {
      cancelled = true; off();
      if (timer !== undefined) window.clearTimeout(timer);
      if (eventTimer !== undefined) window.clearTimeout(eventTimer);
    };
  }, [enabled]);
  return null;
}
